import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../store/AuthContext';
import { Download } from 'lucide-react';

interface ReportData {
  activeResidents: number;
  totalNotes: number;
  notesPeriod: number;
  totalMoods: number;
  residentActivity: { name: string; room?: string; notes: number }[];
}

export function ReportsPage() {
  const { user } = useAuth();
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenantName, setTenantName] = useState('');
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'lastmonth'>('month');

  useEffect(() => { loadReports(); }, [user, dateRange]);

  const loadReports = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase.from('profiles').select('tenant_id').eq('id', user.id).single();
      if (!profile?.tenant_id) { setLoading(false); return; }
      const { data: tenant } = await supabase.from('tenants').select('name').eq('id', profile.tenant_id).single();
      if (tenant) setTenantName(tenant.name);
      const now = new Date();
      let rangeStart: string;
      if (dateRange === 'week') {
        rangeStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (dateRange === 'lastmonth') {
        const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        rangeStart = first.toISOString();
      } else {
        rangeStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      }
      const { count: activeResidents } = await supabase.from('residents').select('*', { count: 'exact', head: true }).eq('tenant_id', profile.tenant_id).eq('status', 'active');
      const { count: totalNotes } = await supabase.from('care_notes').select('*', { count: 'exact', head: true }).eq('tenant_id', profile.tenant_id);
      const { count: notesPeriod } = await supabase.from('care_notes').select('*', { count: 'exact', head: true }).eq('tenant_id', profile.tenant_id).gte('created_at', rangeStart);
      const { count: totalMoods } = await supabase.from('mood_logs').select('*', { count: 'exact', head: true }).eq('tenant_id', profile.tenant_id).gte('created_at', rangeStart);
      const { data: residents } = await supabase.from('residents').select('id, full_name, room_number').eq('tenant_id', profile.tenant_id).eq('status', 'active');
      const residentActivity = await Promise.all(
        (residents || []).map(async r => {
          const { count: notes } = await supabase.from('care_notes').select('*', { count: 'exact', head: true }).eq('tenant_id', profile.tenant_id).eq('resident_id', r.id).gte('created_at', rangeStart);
          return { name: r.full_name || 'Unknown', room: r.room_number || undefined, notes: notes || 0 };
        })
      );
      setData({ activeResidents: activeResidents || 0, totalNotes: totalNotes || 0, notesPeriod: notesPeriod || 0, totalMoods: totalMoods || 0, residentActivity: residentActivity.sort((a, b) => b.notes - a.notes) });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ['DIPPS Facility Report', tenantName, new Date().toLocaleDateString('en-CA')],
      [], ['SUMMARY'],
      ['Active Residents', data.activeResidents], ['Total Notes', data.totalNotes],
      ['Notes This Period', data.notesPeriod], ['Mood Logs', data.totalMoods],
      [], ['RESIDENT ACTIVITY'], ['Resident', 'Notes'],
      ...data.residentActivity.map(r => [r.name, r.notes])
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = 'DIPPS-Report-' + tenantName + '-' + new Date().toLocaleDateString('en-CA') + '.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  const getInsight = () => {
    if (!data) return '';
    if (data.notesPeriod === 0) return 'No notes recorded this period — check documentation compliance.';
    if (data.totalMoods === 0) return 'No mood logs recorded — check compliance.';
    const top2 = data.residentActivity.slice(0, 2);
    const top2Total = top2.reduce((s, r) => s + r.notes, 0);
    if (data.notesPeriod > 0 && top2Total > data.notesPeriod * 0.6) return 'Top 2 residents account for majority of notes — review coverage balance.';
    const zeroNotes = data.residentActivity.filter(r => r.notes === 0).length;
    if (zeroNotes > 0) return zeroNotes + ' resident(s) have no notes this period — review for gaps.';
    return 'Most residents are receiving consistent documentation.';
  };

  const dateLabel = dateRange === 'week' ? 'This Week' : dateRange === 'lastmonth' ? 'Last Month' : 'This Month';
  const monthYear = new Date().toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });

  if (loading) return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">
      <div className="flex items-center justify-center h-[60vh] text-[rgb(var(--admin-text-muted))] text-base">Generating report...</div>
    </main>
  );

  return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[rgb(var(--admin-text-primary))]">Reports</h1>
          <p className="text-sm text-[rgb(var(--admin-text-muted))] mt-1">{tenantName} &middot; {monthYear}</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={dateRange} onChange={e => setDateRange(e.target.value as any)} className="px-4 py-2 rounded-2xl bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] text-sm border border-[rgb(var(--admin-border))]">
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="lastmonth">Last Month</option>
          </select>
          <button onClick={exportCsv} className="px-5 py-3 rounded-2xl bg-[rgb(var(--admin-accent))] text-white text-sm font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity duration-200">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {data && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: 'Active Residents', value: data.activeResidents },
              { label: 'Total Notes', value: data.totalNotes },
              { label: 'Notes ' + dateLabel, value: data.notesPeriod },
              { label: 'Mood Logs', value: data.totalMoods }
            ].map(card => (
              <div key={card.label} className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
                <p className="text-xl font-semibold text-[rgb(var(--admin-text-primary))]">{card.value}</p>
                <p className="text-sm text-[rgb(var(--admin-text-muted))]">{card.label}</p>
              </div>
            ))}
          </div>

          {/* Insight Bar */}
          <div className="rounded-2xl p-4 bg-[rgb(var(--admin-surface-2))] border border-[rgb(var(--admin-border))]">
            <p className="text-sm text-[rgb(var(--admin-text-secondary))]">{getInsight()}</p>
          </div>

          {/* Resident Activity */}
          <div className="rounded-3xl p-5 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm space-y-4">
            <h2 className="text-base font-semibold text-[rgb(var(--admin-text-primary))]">Resident Activity</h2>
            {data.residentActivity.length === 0 ? (
              <p className="text-sm text-[rgb(var(--admin-text-muted))]">No data available for this period</p>
            ) : (
              <div className="space-y-3">
                {data.residentActivity.map((r, i) => {
                  const maxNotes = data.residentActivity[0]?.notes || 1;
                  const pct = Math.max(4, (r.notes / maxNotes) * 100);
                  return (
                    <div key={r.name} className="rounded-2xl px-4 py-3 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] flex items-center justify-between hover:bg-[rgb(var(--admin-surface-2))] transition-colors duration-200">
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-[rgb(var(--admin-text-muted))] w-6 text-right">{i + 1}</span>
                        <div>
                          <p className="text-sm font-medium text-[rgb(var(--admin-text-primary))]">{r.name}</p>
                          {r.room && <p className="text-xs text-[rgb(var(--admin-text-muted))]">Room {r.room}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-[rgb(var(--admin-text-secondary))] w-[70px] text-right">{r.notes} notes</span>
                        <div className="w-[120px] h-2 rounded-full bg-[rgb(var(--admin-surface-2))]">
                          <div className="h-2 rounded-full bg-[rgb(var(--admin-accent))]" style={{ width: pct + '%' }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </main>
  );
}